/**
 * Zustand store for the license request workflow.
 *
 * Manages:
 *   - Paginated request list with status filtering
 *   - Dashboard stat counts
 *   - Request detail (tracks + holder-side deal state)
 *   - Create/respond/request-shell actions
 *   - Realtime subscriptions for live updates
 *
 * All API calls go through the centralized apiClient (auth headers automatic).
 */
import { create } from "zustand";
import { supabase } from "./supabase";
import { leaveRealtimeChannel, uniqueChannelTopic } from "./realtime";
import { apiClient } from "./api-client";
import { getUserErrorMessage } from "./utils/error-messages";
// The licensable pair as a TYPE (the runtime constant is `LICENSABLE_RIGHT_TYPES` in
// RequestTrackOverviewGrid.tsx:65) — imported rather than hand-typed here, which is how the
// backend's own copies of the pair drifted before they were collapsed.
import type { RightSide } from "./licensing/precheck-derivations";
import type {
  LicenseRequestListItem,
  LicenseRequestDetail,
  LicenseDeal,
  LicenseRequestStats,
  LicenseRequestStatus,
  LicenseRequestListResponse,
  CreateLicenseRequestPayload,
  CreateLicenseRequestResponse,
  LicenseDealClearanceUnit,
  LicenseDealMessage,
  RespondLicenseRequestPayload,
  SyncLicenseDealResponse,
  DeclineRequestTrackResponse,
  ReviseLicenseDealGraphPayload,
  ReviseLicenseDealGraphResponse,
  CorrectTrackRightCoveragePayload,
  CorrectTrackRightCoverageResponse,
  DisavowImpact,
  UnlockDealDeliverablesPayload,
  UnlockDealDeliverablesResponse,
  DealContractFileRef,
  DealContractResponse,
  UploadDealDeliverableResponse,
  DealFileUrlResponse,
  CoordinatorRoleContext,
  DeclareCoordinatorHolderPayload,
  DeclareCoordinatorRepresentationPayload,
  DeclareCoordinatorRoleResponse,
  RequestRecipientsResponse,
} from "@/types/license-request";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Negotiation export (PDF) — the frontend half of the per-LANE terms export.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Downloads the negotiation-terms PDF for ONE clearance unit — one lane, one document
 * (`GET /api/licenses/{requestId}/tracks/{requestTrackId}/units/{unitId}/export.pdf`) — and
 * saves it under the server-supplied filename. A label cannot accept a buyer's terms until
 * their recording artists have approved them, so this lets a seller pull the current terms
 * BEFORE accepting and hand them to the approvers — see `ContractHud`'s "Export for approvals"
 * control, which is deliberately available while every term is still merely proposed.
 *
 * Scoped to the unit, not the track, because the approvers differ per lane: a recording artist
 * approves the MASTER, a writer or publisher approves the PUBLISHING. Sending one person both
 * lanes is wrong, and the control lives in the per-SIDE kebab (`SideLifecycleMenu`), so a
 * track-scoped payload there produced the same document under every side's menu.
 *
 * Goes through `apiClient.getBlob` (not a hand-rolled `fetch`) so the Authorization header is
 * attached the same way every other licensing call attaches it, and a non-OK response throws
 * the same `ApiError` callers already handle.
 */
export async function downloadNegotiationExport(
  requestId: string,
  requestTrackId: string,
  unitId: string
): Promise<void> {
  const { blob, filename } = await apiClient.getBlob(
    `/api/licenses/${requestId}/tracks/${requestTrackId}/units/${unitId}/export.pdf`
  );

  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    // Content-Disposition is normally present (the route sets it); fall back to a generated
    // name rather than ever saving a file literally named "undefined". The lane id is part of
    // the fallback: two lanes of one track would otherwise collide in the download folder.
    link.download = filename || `negotiation-terms-${requestTrackId}-${unitId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Request recipients — who a request reaches, read before it exists (DEC-075)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `GET /api/license-requests/recipients` — per track, per side, the holders a request to this seller
 * would reach: confirmed and registry-evidence holders, whether each is on the platform, and the
 * address we hold for an off-platform one (design 2026-10-25-request-recipients-design.md §A).
 * A buyer who is not fully set up gets a 403; the form then names only the seller.
 */
export async function fetchRequestRecipients(
  sellerLabelTag: string,
  trackIds: readonly string[],
  options?: { signal?: AbortSignal }
): Promise<RequestRecipientsResponse> {
  const params = new URLSearchParams({ seller_label_tag: sellerLabelTag });
  for (const trackId of trackIds) params.append("track_id", trackId);
  return apiClient.get<RequestRecipientsResponse>(`/api/license-requests/recipients?${params}`, options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Realtime subscription (module-level, refcounted)
//
// The license-requests realtime channel is shared across all consumers of
// `subscribeToChanges`. Without this, every mounted consumer (and every
// StrictMode mount/cleanup/mount replay) creates a fresh `Date.now()`-named
// channel, leading to duplicate handler invocations on every postgres event
// and (combined with the now-removed clearDetail-in-cleanup) a fetch loop.
// ─────────────────────────────────────────────────────────────────────────────

type SharedRealtimeChannel = ReturnType<typeof supabase.channel>;
let sharedRealtimeChannel: SharedRealtimeChannel | null = null;
let sharedRealtimeRefcount = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Reset generation (sign-out / account switch)
//
// `reset()` runs from `resetAllStores` when one account signs out or another signs in. A read
// that was already in flight belongs to the PREVIOUS account: its answer lands after the wipe and
// would write that account's rows, counts or request view into the next account's store — and a
// request detail is viewer-shaped (`viewer_access_mode`), so the same request id opened by the
// other party would take the first party's view. The follow-up reads of a mutation (a deal fetched
// or spliced in after a write) record this number when they start and drop their answer if a reset
// has happened since; the list, the counts and the detail use the latest-read tokens below.
// ─────────────────────────────────────────────────────────────────────────────

let resetGeneration = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Latest-read tokens (A1.12 / A1.13, 2026-10-01)
//
// The list, the tab counts and the open request each show ONE answer, so only the NEWEST read of
// each may write it. Answers do not land in the order they were asked: switching quickly from the
// Pending tab to Accepted, or from request A to request B, left two reads in flight, and whichever
// answered LAST won — Accepted cards under the Pending tab, A's cards under B's address. And because
// a superseded read also cleared the loading flag, the standalone console (which waits for the
// detail of ITS request) could sit on its placeholder for good once A's answer had replaced B's.
//
// Every read takes the next number when it starts and writes only if no newer read has started
// since. `reset()` moves all three forward too, which is what retires the previous account's reads
// on sign-out — these replace `resetGeneration` for the three reads they cover.
// ─────────────────────────────────────────────────────────────────────────────

let listRead = 0;
let statsRead = 0;
let detailRead = 0;

/**
 * DEC-028 — WHY a coordinator may take a side. Own versus represent decides whether the actor
 * signs as the rights holder or as an agent, which lands on the contract and at payout, so the
 * API requires it. Deliberately narrower than the DB column, which also allows `'unknown'` —
 * a backfill-only value naming lanes taken before anyone was asked. No user may assert it.
 */
export type CoordinatorHandlingBasis = "own" | "represent";

// ─────────────────────────────────────────────────────────────────────────────
// Store Interface
// ─────────────────────────────────────────────────────────────────────────────

interface LicenseRequestsState {
  // List view
  requests: LicenseRequestListItem[];
  total: number;
  hasMore: boolean;
  statusFilter: LicenseRequestStatus | null;

  // Stats
  stats: LicenseRequestStats | null;

  // Detail view
  selectedDetail: LicenseRequestDetail | null;

  // Loading states
  listLoading: boolean;
  statsLoading: boolean;
  detailLoading: boolean;
  creating: boolean;
  responding: boolean;

  // Error states
  listError: string | null;
  detailError: string | null;

  // ── List actions ──────────────────────────────────────────────────────────
  setStatusFilter: (status: LicenseRequestStatus | null) => void;
  fetchRequests: () => Promise<void>;
  fetchMore: () => Promise<void>;

  // ── Stats ─────────────────────────────────────────────────────────────────
  fetchStats: () => Promise<void>;

  // ── Detail ────────────────────────────────────────────────────────────────
  fetchDetail: (requestId: string) => Promise<void>;
  /** Silent re-fetch for realtime signals — no detailLoading flash. */
  refreshDetail: (requestId: string) => Promise<void>;
  fetchDealDetail: (dealId: string) => Promise<LicenseDeal>;
  clearDetail: () => void;

  // ── Mutations ─────────────────────────────────────────────────────────────
  createRequest: (payload: CreateLicenseRequestPayload) => Promise<CreateLicenseRequestResponse>;
  respondToRequest: (requestId: string, payload: RespondLicenseRequestPayload) => Promise<void>;
  withdrawRequest: (requestId: string, reason?: string) => Promise<void>;
  syncDealFromTrack: (requestTrackId: string) => Promise<SyncLicenseDealResponse>;
  /** Refuse ONE requested track, leaving the rest of the request running. */
  declineRequestTrack: (
    requestTrackId: string,
    responseMessage?: string
  ) => Promise<DeclineRequestTrackResponse>;
  reviseDealGraphFromTrack: (
    requestTrackId: string,
    payload?: ReviseLicenseDealGraphPayload
  ) => Promise<ReviseLicenseDealGraphResponse>;
  correctUnitCoverage: (
    dealId: string,
    unitId: string,
    payload: CorrectTrackRightCoveragePayload
  ) => Promise<CorrectTrackRightCoverageResponse>;
  getDisavowImpact: (
    dealId: string,
    unitId: string
  ) => Promise<DisavowImpact>;
  fetchDealRoleContext: (dealId: string) => Promise<CoordinatorRoleContext>;
  declareCoordinatorHolder: (
    dealId: string,
    payload: DeclareCoordinatorHolderPayload
  ) => Promise<DeclareCoordinatorRoleResponse>;
  declareCoordinatorRepresentation: (
    dealId: string,
    payload: DeclareCoordinatorRepresentationPayload
  ) => Promise<DeclareCoordinatorRoleResponse>;
  /** Declare that the coordinator will run ONE side. Reversible, and DECOUPLED from start — the
   *  lane is recorded and the coordinator still has to press Start.
   *
   *  `"all"` is deliberately NOT expressible here: that is one-stop, which OPENS the negotiation
   *  and navigates away. Two decisions of very different weight behind one union meant any per-side
   *  call site was one bad `scope` value away from silently opening a deal. Use
   *  `coordinatorHandleOneStop`. `RightSide` is the type-only statement of the licensable pair —
   *  the runtime constant is `LICENSABLE_RIGHT_TYPES` (RequestTrackOverviewGrid.tsx:65), itself
   *  mirroring the backend's (services/licensing/negotiation.py:122). */
  coordinatorHandleSide: (
    requestTrackId: string,
    rightType: RightSide,
    basis: CoordinatorHandlingBasis
  ) => Promise<DeclareCoordinatorRoleResponse>;
  /** One-stop: the coordinator runs the ENTIRE track. Unlike the per-side call this IS the start —
   *  the deal opens on success. */
  coordinatorHandleOneStop: (
    requestTrackId: string,
    basis: CoordinatorHandlingBasis
  ) => Promise<DeclareCoordinatorRoleResponse>;
  unlockDealDeliverables: (
    dealId: string,
    payload?: UnlockDealDeliverablesPayload
  ) => Promise<UnlockDealDeliverablesResponse>;
  /** Send a side's contract (spec 2026-10-26 §6): v1 when the side has none, else the next
   *  version. `note` is the seller's optional line to the buyer. */
  addDealContractDocument: (
    dealId: string,
    unitId: string,
    file: File,
    note?: string
  ) => Promise<DealContractResponse>;
  /** The buyer asks the side's holder for changes to the current version, with one note. */
  requestContractChanges: (
    dealId: string,
    contractId: string,
    note: string
  ) => Promise<DealContractResponse>;
  /** The buyer returns the signed copy of the current version; the contract is then final. */
  uploadSignedContractCopy: (
    dealId: string,
    contractId: string,
    file: File
  ) => Promise<DealContractResponse>;
  /** A contract file's short-lived URL: the sent file (default) or the signed copy, of the current
   *  version or an earlier one (`version`, sent files only). */
  getDealContractDocumentUrl: (
    dealId: string,
    contractId: string,
    file?: DealContractFileRef
  ) => Promise<DealFileUrlResponse>;
  getDealPaymentReceiptUrl: (
    dealId: string,
    paymentId: string
  ) => Promise<DealFileUrlResponse>;
  uploadDealDeliverable: (
    dealId: string,
    file: File,
    deliverableType: string,
    name?: string
  ) => Promise<UploadDealDeliverableResponse>;
  getDealDeliverableUrl: (
    dealId: string,
    deliverableId: string
  ) => Promise<DealFileUrlResponse>;

  // ── Realtime ──────────────────────────────────────────────────────────────
  subscribeToChanges: () => () => void;

  // ── Reset ─────────────────────────────────────────────────────────────────
  reset: () => void;
}

/**
 * Re-read the deal after a contract write, without letting the read decide the write's outcome. The
 * write has already landed when this runs: a failed re-read reported as a failed send led the seller to
 * send again, and every send is a new version that is kept forever (spec 2026-10-26 §5.1, no delete).
 */
async function refreshDealAfterContractWrite(
  get: () => Pick<LicenseRequestsState, "fetchDealDetail">,
  dealId: string
): Promise<void> {
  try {
    await get().fetchDealDetail(dealId);
  } catch (error) {
    console.warn(`Contract saved, but re-reading deal ${dealId} failed:`, error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Initial State
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
  requests: [] as LicenseRequestListItem[],
  total: 0,
  hasMore: true,
  statusFilter: null as LicenseRequestStatus | null,
  stats: null as LicenseRequestStats | null,
  selectedDetail: null as LicenseRequestDetail | null,
  listLoading: false,
  statsLoading: false,
  detailLoading: false,
  creating: false,
  responding: false,
  listError: null as string | null,
  detailError: null as string | null,
};

/** One tab's count from the list endpoint, or null when it could not be read. Never a stand-in 0:
 *  a failed call used to return 0, and the tab printed it as a real count. */
async function fetchLicenseRequestCount(
  status: LicenseRequestStatus | null
): Promise<number | null> {
  try {
    const params = new URLSearchParams({
      limit: "1",
      offset: "0",
    });
    if (status) params.set("status", status);

    const data = await apiClient.get<LicenseRequestListResponse>(
      `/api/license-requests?${params}`
    );
    return typeof data.total === "number" ? data.total : null;
  } catch {
    return null;
  }
}

/** The largest of the counts that were actually read; null when none was. */
function knownCount(...counts: Array<number | null | undefined>): number | null {
  const known = counts.filter((count): count is number => typeof count === "number");
  return known.length > 0 ? Math.max(...known) : null;
}

function upsertDealIntoDetail(
  detail: LicenseRequestDetail | null,
  nextDeal: LicenseDeal
): LicenseRequestDetail | null {
  if (!detail) return detail;
  const existingIndex = detail.deals.findIndex((deal) => deal.id === nextDeal.id);
  if (existingIndex === -1) {
    return {
      ...detail,
      deals: [...detail.deals, nextDeal].sort((left, right) =>
        String(left.created_at || "").localeCompare(String(right.created_at || ""))
      ),
    };
  }

  const nextDeals = [...detail.deals];
  nextDeals[existingIndex] = nextDeal;
  return {
    ...detail,
    deals: nextDeals,
  };
}

type UnitMutationResult = {
  deal: Partial<LicenseDeal> & Pick<LicenseDeal, "id">;
  unit: Partial<LicenseDealClearanceUnit> & Pick<LicenseDealClearanceUnit, "id">;
  message?: LicenseDealMessage | null;
};

function mergeUnitMutationIntoDetail(
  detail: LicenseRequestDetail | null,
  result: UnitMutationResult
): LicenseRequestDetail | null {
  if (!detail) return detail;

  const dealIndex = detail.deals.findIndex((deal) => deal.id === result.deal.id);
  if (dealIndex === -1) return detail;

  const existingDeal = detail.deals[dealIndex];
  const unitIndex = existingDeal.clearance_units.findIndex((unit) => unit.id === result.unit.id);
  if (unitIndex === -1) {
    return upsertDealIntoDetail(detail, {
      ...existingDeal,
      ...result.deal,
      clearance_units: existingDeal.clearance_units,
      system_messages: existingDeal.system_messages,
      contracts: existingDeal.contracts,
      payments: existingDeal.payments,
      deliverables: existingDeal.deliverables,
    } as LicenseDeal);
  }

  const existingUnit = existingDeal.clearance_units[unitIndex];
  const nextMessages = Array.isArray(result.unit.messages)
    ? result.unit.messages
    : [...existingUnit.messages];
  if (result.message && !nextMessages.some((message) => message.id === result.message?.id)) {
    nextMessages.push(result.message);
  }

  const nextUnit: LicenseDealClearanceUnit = {
    ...existingUnit,
    ...result.unit,
    approvers: Array.isArray(result.unit.approvers)
      ? result.unit.approvers
      : existingUnit.approvers,
    messages: nextMessages,
  };

  const nextUnits = [...existingDeal.clearance_units];
  nextUnits[unitIndex] = nextUnit;

  const nextDeals = [...detail.deals];
  nextDeals[dealIndex] = {
    ...existingDeal,
    ...result.deal,
    clearance_units: nextUnits,
    system_messages: Array.isArray(result.deal.system_messages)
      ? result.deal.system_messages
      : existingDeal.system_messages,
    contracts: Array.isArray(result.deal.contracts)
      ? result.deal.contracts
      : existingDeal.contracts,
    payments: Array.isArray(result.deal.payments)
      ? result.deal.payments
      : existingDeal.payments,
    deliverables: Array.isArray(result.deal.deliverables)
      ? result.deal.deliverables
      : existingDeal.deliverables,
  } as LicenseDeal;

  return {
    ...detail,
    deals: nextDeals,
  };
}

/**
 * The one POST behind both coordinator-handling doors.
 *
 * Task 37 start-flow: the coordinator runs a lane without claiming rights. Bootstraps the deal (if
 * needed) and opens a coordinator-handled lane.
 *
 * DEC-028: `basis` is REQUIRED by the API — it records WHY the lane may be taken (own vs represent),
 * which decides whether the actor signs as the rights holder or as an agent. It used to be asked in
 * the modal and then dropped on the floor.
 *
 * The scope stays a private parameter: callers reach it through `coordinatorHandleSide` (one side,
 * reversible) or `coordinatorHandleOneStop` (the whole track, opens the deal), never by naming
 * `"all"` themselves.
 */
async function postCoordinatorHandling(
  get: () => LicenseRequestsState,
  requestTrackId: string,
  path: string,
  body: Record<string, unknown>
): Promise<DeclareCoordinatorRoleResponse> {
  const result = await apiClient.post<DeclareCoordinatorRoleResponse>(
    `/api/license-request-tracks/${requestTrackId}/${path}`,
    body
  );
  // Guard the id itself: with no detail open AND no context echoed back, `undefined === undefined`
  // used to be true and sent a refresh to /api/license-requests/undefined.
  const requestId = result.context?.request_id;
  if (requestId && get().selectedDetail?.id === requestId) {
    await get().refreshDetail(requestId);
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useLicenseRequestsStore = create<LicenseRequestsState>(
  (set, get) => ({
    ...initialState,

    // ── List actions ────────────────────────────────────────────────────────

    setStatusFilter: (status) => {
      set({ statusFilter: status, requests: [], total: 0, hasMore: true });
      get().fetchRequests();
    },

    fetchRequests: async () => {
      const { statusFilter } = get();
      const read = ++listRead;
      set({ listLoading: true, listError: null });

      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set("status", statusFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", "0");

        const data = await apiClient.get<LicenseRequestListResponse>(
          `/api/license-requests?${params}`
        );
        // A newer read (another tab, a realtime refetch, a sign-out) owns the list now.
        if (read !== listRead) return;

        set({
          requests: data.items,
          total: data.total,
          hasMore: data.has_more,
          listLoading: false,
        });
      } catch (err: any) {
        if (read !== listRead) return;
        set({
          listError: getUserErrorMessage(err, "Couldn't load license requests. Please retry."),
          listLoading: false,
        });
      }
    },

    fetchMore: async () => {
      const { requests, hasMore, listLoading, statusFilter } = get();
      if (!hasMore || listLoading) return;

      // A page of the CURRENT tab: switching tabs while it is in flight starts a newer read, and
      // this page — the old tab's rows — must not be appended to the new tab's list.
      const read = ++listRead;
      set({ listLoading: true });

      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set("status", statusFilter);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(requests.length));

        const data = await apiClient.get<LicenseRequestListResponse>(
          `/api/license-requests?${params}`
        );
        if (read !== listRead) return;

        set((state) => {
          // Deduplicate in case of realtime race
          const existingIds = new Set(state.requests.map((r) => r.id));
          const newItems = data.items.filter((r) => !existingIds.has(r.id));
          return {
            requests: [...state.requests, ...newItems],
            total: data.total,
            hasMore: data.has_more,
            listLoading: false,
          };
        });
      } catch (err: any) {
        if (read !== listRead) return;
        set({
          listError: getUserErrorMessage(err, "Couldn't load more requests. Please retry."),
          listLoading: false,
        });
      }
    },

    // ── Stats ───────────────────────────────────────────────────────────────

    fetchStats: async () => {
      const read = ++statsRead;
      set({ statsLoading: true });

      try {
        const [apiStats, allCount, pendingCount, negotiatingCount, acceptedCount, rejectedCount] =
          await Promise.all([
            apiClient.get<LicenseRequestStats>("/api/license-requests/stats").catch(() => null),
            fetchLicenseRequestCount(null),
            fetchLicenseRequestCount("pending"),
            fetchLicenseRequestCount("negotiating"),
            fetchLicenseRequestCount("accepted"),
            fetchLicenseRequestCount("rejected"),
          ]);
        if (read !== statsRead) return;

        // Each count is known when either source answered for it. The four statuses also floor the
        // total, as before — but only a total that was itself read is ever shown.
        const pending = knownCount(apiStats?.pending, pendingCount);
        const negotiating = knownCount(apiStats?.negotiating, negotiatingCount);
        const accepted = knownCount(apiStats?.accepted, acceptedCount);
        const rejected = knownCount(apiStats?.rejected, rejectedCount);
        const readTotal = knownCount(apiStats?.total, allCount);

        // ANY count unknown → no counts at all (`stats: null`), which every tab renders as "–".
        // Both reads for a status failing used to print "0" on that tab: a number nobody counted.
        // `LicenseRequestStats` has no per-tab "unknown", so it is all or none.
        if (
          pending === null ||
          negotiating === null ||
          accepted === null ||
          rejected === null ||
          readTotal === null
        ) {
          set({ stats: null, statsLoading: false });
          return;
        }

        set({
          stats: {
            pending,
            negotiating,
            accepted,
            rejected,
            total: Math.max(readTotal, pending + negotiating + accepted + rejected),
          },
          statsLoading: false,
        });
      } catch {
        if (read !== statsRead) return;
        set({ stats: null, statsLoading: false });
      }
    },

    // ── Detail ──────────────────────────────────────────────────────────────

    fetchDetail: async (requestId) => {
      const read = ++detailRead;
      set({ detailLoading: true, detailError: null });

      try {
        const detail = await apiClient.get<LicenseRequestDetail>(
          `/api/license-requests/${requestId}`
        );
        // A newer read — another request opened, the open one closed, or a sign-out — owns the
        // detail now. Its own answer clears the loading flag.
        if (read !== detailRead) return;
        set({ selectedDetail: detail, detailLoading: false });
      } catch (err: any) {
        if (read !== detailRead) return;
        set({
          detailError: err.message || "Failed to fetch request detail",
          detailLoading: false,
        });
      }
    },

    refreshDetail: async (requestId) => {
      // A silent refresh never takes the detail over: it reuses the current token rather than
      // taking a new one, so it lands only if no newer read (or reset) started meanwhile — and only
      // onto the same request.
      const read = detailRead;
      try {
        const detail = await apiClient.get<LicenseRequestDetail>(
          `/api/license-requests/${requestId}`
        );
        if (read !== detailRead) return;
        set((state) =>
          state.selectedDetail?.id === requestId ? { selectedDetail: detail } : {}
        );
      } catch {
        // Background refresh: keep the current detail; an explicit fetch will
        // surface errors through detailError.
      }
    },

    fetchDealDetail: async (dealId) => {
      const generation = resetGeneration;
      const deal = await apiClient.get<LicenseDeal>(`/api/license-deals/${dealId}`);
      // Only merge if the deal belongs to the request currently in view — otherwise a slow
      // fetch resolving after the user navigated to another request bleeds it into the wrong
      // detail (matches the refreshDetail guard above). Still return the deal for the caller.
      if (generation !== resetGeneration) return deal;
      set((state) =>
        state.selectedDetail && deal.request_id === state.selectedDetail.id
          ? { selectedDetail: upsertDealIntoDetail(state.selectedDetail, deal) }
          : {}
      );
      return deal;
    },

    // Closing the open request also retires any read of it still in flight, so its answer cannot
    // land after the close and reopen it. That read can no longer clear the loading flag, so this does.
    clearDetail: () => {
      detailRead += 1;
      set({ selectedDetail: null, detailError: null, detailLoading: false });
    },

    // ── Mutations ───────────────────────────────────────────────────────────

    createRequest: async (payload) => {
      set({ creating: true });

      try {
        const result = await apiClient.post<CreateLicenseRequestResponse>(
          "/api/license-requests",
          payload
        );
        set({ creating: false });

        // Refresh list and stats after creation
        get().fetchRequests();
        get().fetchStats();

        return result;
      } catch (err: any) {
        set({ creating: false });
        throw err;
      }
    },

    respondToRequest: async (requestId, payload) => {
      set({ responding: true });

      try {
        await apiClient.post(
          `/api/license-requests/${requestId}/respond`,
          payload
        );
        set({ responding: false });

        // Refresh the detail if we're looking at it
        const { selectedDetail } = get();
        if (selectedDetail?.id === requestId) {
          get().fetchDetail(requestId);
        }

        // Refresh list and stats
        get().fetchRequests();
        get().fetchStats();
      } catch (err: any) {
        set({ responding: false });
        throw err;
      }
    },

    withdrawRequest: async (requestId, reason) => {
      set({ responding: true });

      try {
        await apiClient.post(
          `/api/license-requests/${requestId}/withdraw`,
          { reason: reason || null }
        );
        set({ responding: false });

        // Refresh the detail if we're looking at it
        const { selectedDetail } = get();
        if (selectedDetail?.id === requestId) {
          get().fetchDetail(requestId);
        }

        // Refresh list and stats
        get().fetchRequests();
        get().fetchStats();
      } catch (err: any) {
        set({ responding: false });
        throw err;
      }
    },

    declineRequestTrack: async (requestTrackId, responseMessage) => {
      const result = await apiClient.post<DeclineRequestTrackResponse>(
        `/api/license-request-tracks/${requestTrackId}/decline`,
        { reason: responseMessage?.trim() || null }
      );
      // Deliberately no local patch of `selectedDetail`. A decline can cancel that track's deal,
      // revoke its invites AND close the whole request (`request_status`) — the caller refetches
      // the detail rather than this store trying to reproduce a multi-table server transaction
      // from one response. `syncDealFromTrack` can splice because it returns exactly one deal.
      return result;
    },

    syncDealFromTrack: async (requestTrackId) => {
      try {
        const generation = resetGeneration;
        const result = await apiClient.post<SyncLicenseDealResponse>(
          `/api/license-request-tracks/${requestTrackId}/sync-deal`,
          {}
        );
        if (generation !== resetGeneration) return result;

        const { selectedDetail } = get();
        if (selectedDetail?.id === result.request_id) {
          set((state) => ({
            selectedDetail: upsertDealIntoDetail(state.selectedDetail, result.deal),
          }));
        }

        return result;
      } catch (err: any) {
        throw err;
      }
    },

    reviseDealGraphFromTrack: async (requestTrackId, payload) => {
      try {
        const generation = resetGeneration;
        const result = await apiClient.post<ReviseLicenseDealGraphResponse>(
          `/api/license-request-tracks/${requestTrackId}/revise-deal-graph`,
          payload ?? {}
        );
        if (generation !== resetGeneration) return result;

        const { selectedDetail } = get();
        if (selectedDetail?.id === result.request_id) {
          set((state) => ({
            selectedDetail: upsertDealIntoDetail(state.selectedDetail, result.deal),
          }));
        }

        return result;
      } catch (err: any) {
        throw err;
      }
    },

    correctUnitCoverage: async (dealId, unitId, payload) => {
      const result = await apiClient.post<CorrectTrackRightCoverageResponse>(
        `/api/license-deals/${dealId}/units/${unitId}/coverage`,
        payload
      );

      const { selectedDetail } = get();
      if (selectedDetail?.id === result.request_id) {
        await get().fetchDealDetail(dealId);
      }
      return result;
    },

    getDisavowImpact: async (dealId, unitId) => {
      return apiClient.get<DisavowImpact>(
        `/api/license-deals/${dealId}/units/${unitId}/disavow-impact`
      );
    },

    fetchDealRoleContext: async (dealId) => {
      return apiClient.get<CoordinatorRoleContext>(
        `/api/license-deals/${dealId}/role-context`
      );
    },

    declareCoordinatorHolder: async (dealId, payload) => {
      const result = await apiClient.post<DeclareCoordinatorRoleResponse>(
        `/api/license-deals/${dealId}/declare-holder`,
        payload
      );
      // A new confirmed right + clearance unit was written into the deal graph;
      // refresh the request detail so the new holder side shows up immediately.
      const { selectedDetail } = get();
      if (selectedDetail?.id === result.context?.request_id) {
        await get().refreshDetail(result.context.request_id);
      }
      return result;
    },

    declareCoordinatorRepresentation: async (dealId, payload) => {
      const result = await apiClient.post<DeclareCoordinatorRoleResponse>(
        `/api/license-deals/${dealId}/declare-representation`,
        payload
      );
      const { selectedDetail } = get();
      if (selectedDetail?.id === result.context?.request_id) {
        await get().refreshDetail(result.context.request_id);
      }
      return result;
    },

    coordinatorHandleSide: async (requestTrackId, rightType, basis) =>
      postCoordinatorHandling(get, requestTrackId, "handle-side", {
        right_type: rightType,
        basis,
      }),

    // TWO ENDPOINTS, not one endpoint with a scope. `handle-side` no longer accepts `"all"` —
    // the Literal was narrowed to master|publishing so the bundled one-stop path is
    // UNREPRESENTABLE from a per-side call rather than guarded by a runtime `if` a later change
    // could relax. One-stop therefore has its own door, and its body carries no scope at all
    // (the declaration IS the scope). Posting `{right_type:"all"}` here 422s at Pydantic.
    coordinatorHandleOneStop: async (requestTrackId, basis) =>
      postCoordinatorHandling(get, requestTrackId, "one-stop", { basis }),


    unlockDealDeliverables: async (dealId, payload) => {
      const result = await apiClient.post<UnlockDealDeliverablesResponse>(
        `/api/license-deals/${dealId}/deliverables/unlock`,
        payload ?? {}
      );

      const { selectedDetail } = get();
      if (selectedDetail?.id === result.deal.request_id) {
        await get().fetchDealDetail(dealId);
      }
      return result;
    },

    addDealContractDocument: async (dealId, unitId, file, note) => {
      const trimmed = note?.trim();
      const result = await apiClient.uploadFile<DealContractResponse>(
        `/api/license-deals/${dealId}/contracts/documents`,
        file,
        { unit_id: unitId, ...(trimmed ? { note: trimmed } : {}) }
      );
      await refreshDealAfterContractWrite(get, dealId);
      return result;
    },

    requestContractChanges: async (dealId, contractId, note) => {
      const result = await apiClient.post<DealContractResponse>(
        `/api/license-deals/${dealId}/contracts/${contractId}/request-changes`,
        { note: note.trim() }
      );
      await refreshDealAfterContractWrite(get, dealId);
      return result;
    },

    uploadSignedContractCopy: async (dealId, contractId, file) => {
      const result = await apiClient.uploadFile<DealContractResponse>(
        `/api/license-deals/${dealId}/contracts/${contractId}/signed-copy`,
        file
      );
      await refreshDealAfterContractWrite(get, dealId);
      return result;
    },

    getDealContractDocumentUrl: async (dealId, contractId, file) => {
      const query = new URLSearchParams();
      if (file?.kind) query.set("kind", file.kind);
      if (file?.version != null) query.set("version", String(file.version));
      const qs = query.toString();
      return apiClient.get<DealFileUrlResponse>(
        `/api/license-deals/${dealId}/contracts/${contractId}/document${qs ? `?${qs}` : ""}`
      );
    },




    getDealPaymentReceiptUrl: async (dealId, paymentId) =>
      apiClient.get<DealFileUrlResponse>(
        `/api/license-deals/${dealId}/payments/${paymentId}/receipt`
      ),

    uploadDealDeliverable: async (dealId, file, deliverableType, name) => {
      const result = await apiClient.uploadFile<UploadDealDeliverableResponse>(
        `/api/license-deals/${dealId}/deliverables/upload`,
        file,
        { deliverable_type: deliverableType, ...(name ? { name } : {}) }
      );
      await get().fetchDealDetail(dealId);
      return result;
    },

    getDealDeliverableUrl: async (dealId, deliverableId) =>
      apiClient.get<DealFileUrlResponse>(
        `/api/license-deals/${dealId}/deliverables/${deliverableId}/file`
      ),

    // ── Realtime ────────────────────────────────────────────────────────────

    subscribeToChanges: () => {
      sharedRealtimeRefcount += 1;

      if (!sharedRealtimeChannel) {
        sharedRealtimeChannel = supabase
          .channel(uniqueChannelTopic("license-requests-changes"))
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "license_requests",
            },
            (payload) => {
              const { eventType, new: newRow } = payload as any;

              if (eventType === "UPDATE" && newRow) {
                // Realtime delivers the RAW table row: unsanitized (the API
                // nulls total_value for sellers) and not API-shaped. Treat it
                // as a change signal — merge only safe whitelisted fields into
                // the list and re-fetch the detail through the sanitizing API.
                set((state) => {
                  const idx = state.requests.findIndex((r) => r.id === newRow.id);
                  if (idx === -1) return state;

                  // A status tab lists only its status (the list filters on this same column). A
                  // request that moved on leaves the tab rather than staying under it with the new
                  // status: an Accepted card under Pending.
                  if (state.statusFilter && newRow.status && newRow.status !== state.statusFilter) {
                    return {
                      requests: state.requests.filter((r) => r.id !== newRow.id),
                      total: Math.max(0, state.total - 1),
                    };
                  }

                  const next = [...state.requests];
                  next[idx] = {
                    ...next[idx],
                    status: newRow.status ?? next[idx].status,
                    outcome_reason: newRow.outcome_reason ?? next[idx].outcome_reason,
                    outcome_by: newRow.outcome_by ?? next[idx].outcome_by,
                    outcome_by_org_id: newRow.outcome_by_org_id ?? next[idx].outcome_by_org_id,
                    outcome_at: newRow.outcome_at ?? next[idx].outcome_at,
                    outcome_actor_role: newRow.outcome_actor_role ?? next[idx].outcome_actor_role,
                    updated_at: newRow.updated_at ?? next[idx].updated_at,
                  };
                  return { requests: next };
                });

                // Re-fetch detail (silently) if viewing this request
                const { selectedDetail } = get();
                if (selectedDetail?.id === newRow.id) {
                  get().refreshDetail(newRow.id);
                }

                // Refresh stats since status may have changed
                get().fetchStats();
              }

              if (eventType === "INSERT" && newRow) {
                // New request — refresh the list to include it with proper scoping
                get().fetchRequests();
                get().fetchStats();
              }
            }
          )
          .subscribe();
      }

      // Each caller gets its own release function. The channel is torn down only
      // when the last subscriber releases it.
      let released = false;
      return () => {
        if (released) return;
        released = true;
        sharedRealtimeRefcount = Math.max(0, sharedRealtimeRefcount - 1);
        if (sharedRealtimeRefcount === 0 && sharedRealtimeChannel) {
          // Only this channel: removeChannel() would close every channel in the app (lib/realtime.ts).
          leaveRealtimeChannel(sharedRealtimeChannel);
          sharedRealtimeChannel = null;
        }
      };
    },

    // ── Reset ───────────────────────────────────────────────────────────────

    reset: () => {
      // Retire every read still in flight for the account being signed out (see resetGeneration
      // and the latest-read tokens).
      resetGeneration += 1;
      listRead += 1;
      statsRead += 1;
      detailRead += 1;
      set(initialState);
    },
  })
);
